/**
 * Local persistence (localStorage) so a reload never loses the timer.
 * Every access is wrapped: a failing storage must never break the timer.
 */
import { isPresetId, type TimerState } from '../../shared/state.js';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const PREFIX = 'stage-timer:';
export const KEYS = {
  state: `${PREFIX}state`,
  room: `${PREFIX}room`,
  pin: `${PREFIX}pin`,
  clientId: `${PREFIX}client-id`,
} as const;

export function parseStoredState(raw: unknown): TimerState | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const s = raw as Record<string, unknown>;
  const num = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  if (!isPresetId(s.presetId)) return null;
  if (!num(s.durationMs) || !num(s.remainingMs) || !num(s.version) || !num(s.updatedAt))
    return null;
  if (s.status !== 'idle' && s.status !== 'running' && s.status !== 'paused') return null;
  if (s.endAt !== null && !num(s.endAt)) return null;
  if (typeof s.originId !== 'string' || s.originId.length === 0) return null;
  return {
    presetId: s.presetId,
    durationMs: s.durationMs as number,
    status: s.status,
    remainingMs: s.remainingMs as number,
    endAt: s.endAt as number | null,
    version: s.version as number,
    updatedAt: s.updatedAt as number,
    originId: s.originId,
  };
}

export interface AppStorage {
  loadState(): TimerState | null;
  saveState(state: TimerState): void;
  loadRoom(): string | null;
  saveRoom(room: string): void;
  loadPin(): string | null;
  savePin(pin: string): void;
  clearPin(): void;
  getOrCreateClientId(prefix: string): string;
}

function randomId(): string {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function createStorage(backend: StorageLike | null): AppStorage {
  const get = (key: string): string | null => {
    try {
      return backend?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };
  const set = (key: string, value: string): void => {
    try {
      backend?.setItem(key, value);
    } catch {
      /* quota / private mode: ignore, timer keeps working in memory */
    }
  };
  const remove = (key: string): void => {
    try {
      backend?.removeItem(key);
    } catch {
      /* ignore */
    }
  };

  return {
    loadState() {
      const raw = get(KEYS.state);
      if (raw === null) return null;
      try {
        return parseStoredState(JSON.parse(raw));
      } catch {
        return null;
      }
    },
    saveState(state) {
      set(KEYS.state, JSON.stringify(state));
    },
    loadRoom: () => get(KEYS.room),
    saveRoom: (room) => set(KEYS.room, room),
    loadPin: () => get(KEYS.pin),
    savePin: (pin) => set(KEYS.pin, pin),
    clearPin: () => remove(KEYS.pin),
    getOrCreateClientId(prefix) {
      const existing = get(KEYS.clientId);
      if (existing) return existing;
      const id = `${prefix}-${randomId()}`;
      set(KEYS.clientId, id);
      return id;
    },
  };
}

export function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}
